import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Check, X, Minus } from 'lucide-react';
import { Button } from '../../../components/ui';
import { Textarea } from '../../../components/ui/Input';

/**
 * SentenceWriting — productive review format. Student writes a sentence
 * using the target word; AI grades on usage + understanding and returns
 * a tier + short feedback.
 *
 * Props:
 *   lemma       — target word
 *   definition  — primary definition (shown as a hint)
 *   onAnswer    — ({ text, elapsed_ms }) => Promise<{
 *                   correct, rating (1/2/3), feedback
 *                 }>
 *   prereqFallback — { lemma, card_id } | null/undefined. Set on the answer
 *                     response when this card's word has a simpler prereq
 *                     form and the attempt graded :again (spec §4.8) — the
 *                     harder card gets suspended server-side and the
 *                     prereq surfaces next. Purely informational here.
 */
export default function SentenceWriting({ lemma, definition, prompt, onAnswer, prereqFallback }) {
  const [text, setText] = useState('');
  const [result, setResult] = useState(null); // null | { tier, feedback }
  const [loading, setLoading] = useState(false);
  const startTime = useRef(Date.now());
  const textareaRef = useRef(null);

  useEffect(() => {
    startTime.current = Date.now();
    setText('');
    setResult(null);
    textareaRef.current?.focus();
  }, [lemma]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!text.trim() || result || loading) return;

    setLoading(true);
    try {
      const elapsed = Date.now() - startTime.current;
      const res = await onAnswer({ text: text.trim(), elapsed_ms: elapsed });
      const rating = res?.rating;
      const tier = rating === 3 ? 'good' : rating === 2 ? 'hard' : 'again';
      setResult({ tier, feedback: res?.feedback || null });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 max-w-xl mx-auto">
      <div className="text-center">
        <h2 className="font-serif text-2xl text-gray-900 dark:text-white mb-1">
          {lemma}
        </h2>
        {definition && (
          <p className="text-base text-gray-500 dark:text-gray-400">{definition}</p>
        )}
      </div>

      <p className="text-lg text-gray-700 dark:text-gray-300 text-center">
        {prompt || (
          <>
            Use{' '}
            <span className="font-serif font-semibold text-gray-900 dark:text-white">
              {lemma}
            </span>{' '}
            in a sentence.
          </>
        )}
      </p>

      {!result ? (
        <form onSubmit={handleSubmit} className="space-y-3">
          <Textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Write your sentence..."
            rows={3}
            spellCheck={false}
            data-gramm="false"
            data-gramm_editor="false"
            // Stable key for the live-session typing mirror.
            data-live-input-key="sentence-writing"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            disabled={loading}
          />
          <div className="flex justify-center">
            <Button type="submit" disabled={!text.trim() || loading}>
              {loading ? 'Grading…' : 'Submit'}
            </Button>
          </div>
        </form>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="space-y-3"
        >
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              &ldquo;{text}&rdquo;
            </p>
          </div>
          <div className="flex items-start gap-2">
            {result.tier === 'good' && (
              <Check
                className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5"
                strokeWidth={2.5}
              />
            )}
            {result.tier === 'hard' && (
              <Minus
                className="w-4 h-4 text-amber-500 dark:text-amber-400 flex-shrink-0 mt-0.5"
                strokeWidth={2.5}
              />
            )}
            {result.tier === 'again' && (
              <X
                className="w-4 h-4 text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5"
                strokeWidth={2.5}
              />
            )}
            {result.feedback && (
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {result.feedback}
              </p>
            )}
          </div>
          {result.tier === 'again' && prereqFallback && (
            <p className="text-sm text-gray-500 dark:text-gray-400 italic">
              Let&rsquo;s practice{' '}
              <span className="font-serif not-italic font-semibold text-gray-700 dark:text-gray-300">
                {prereqFallback.lemma}
              </span>{' '}
              first — we&rsquo;ll come back to {lemma} soon.
            </p>
          )}
        </motion.div>
      )}
    </div>
  );
}
